import React, { useCallback, useMemo } from 'react';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { GridActionsCellItem, GridActionsColDef, GridApiRef, GridRowParams } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { CtxMenuItem } from '../AdminDataGrid/AdminDataGridCtxMenu';
import { MoreVert } from '@mui/icons-material';

const useStyles = makeStyles(() =>
  createStyles({
    paper: {
      border: '1px solid #d3d4d5',
      outline: 0,
    },
    contextMenu: {
      pointerEvents: 'none',
    },
    contextMenuPaper: {
      pointerEvents: 'auto',
    },
  })
);

type Props = {
  open: boolean;
  testId?: string;
  anchorEl?: Element;
  X?: number;
  Y?: number;
  children?: React.ReactElement | React.ReactElement[];
  onClose(): void;
};

export default function EditPanelContextMenu(props: Props) {
  const classes = useStyles();

  const { children, open, X, Y, onClose, anchorEl } = props;

  let anchorPosition = undefined;
  if (typeof props.Y === 'number' && typeof props.X === 'number') {
    anchorPosition = {
      top: props.Y,
      left: props.X,
    };
  }

  return (
    <>
      {X && Y ? (
        <Menu
          anchorReference="anchorPosition"
          anchorPosition={anchorPosition}
          id="row-context-menu"
          test-id='row_context_menu'
          PopoverClasses={{
            root: classes.contextMenu,
            paper: classes.contextMenuPaper,
          }}
          keepMounted
          open={open}
          onClose={onClose}
        >
          {children}
        </Menu>
      ) : (
        <Menu
          className={classes.paper}
          elevation={1}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          id= 'row-context-menu'
          test-id='row_context_menu'
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={onClose}
        >
          {children}
        </Menu>
      )}
    </>
  );
}

export function useContextMenu<T>(apiRef: GridApiRef, testId?: string) {
  const [ctxMenuOpen, setCtxMenuOpen] = useState(false);
  const [currentCtxItem, setCurrentCtxItem] = useState<T>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [coordinatesOfMenu, setCoordinatesOfMenu] = useState<{
    X?: number;
    Y?: number;
  }>({});

  const handleCloseCtxMenu = useCallback(()=>{
    setCtxMenuOpen(false);
    setAnchorEl(undefined);
    setCurrentCtxItem(undefined);
  }, []);

  const handleContextMenu = useCallback((e: any)=>{
    e.preventDefault();
    setCtxMenuOpen(false);
    const id = e.currentTarget.getAttribute('data-id');
    if (e?.currentTarget?.dataset?.rowindex) {
      setCoordinatesOfMenu({ X: e.clientX, Y: e.clientY });
      setCurrentCtxItem(apiRef.current.getRow(id) as unknown as T);
      setCtxMenuOpen(true);
      setAnchorEl(e.target);
    }
  }, [apiRef]);

  const actionColumn: GridActionsColDef = useMemo(() => ({
    field: 'action',
    type: 'actions',
    width: 45,
    getActions: (params: GridRowParams) => [
      <GridActionsCellItem
        icon={<MoreVert test-id={testId ? `${testId}_moreVertIcon` : '_moreVertIcon'}/>}
        testId={testId ? `${testId}_edit_panel_cellItem_action` : 'edit_panel_cellItem_action'}
        label={'actions'}
        showInMenu={false}
        onClick={(evt) => {
          setCtxMenuOpen(true);
          setAnchorEl(evt.currentTarget);
          setCoordinatesOfMenu({ X: undefined, Y: undefined });
          evt.stopPropagation();
          setCurrentCtxItem!(params.row as unknown as T);
        }}
      />,
    ],
  }), [testId]);

  const DataGridContextMenu = React.memo((props: { children?: React.ReactElement }) => {
    return (
      <EditPanelContextMenu   
        test-id={testId ? `${testId}_edit_panel_context_menu` : 'edit_panel_context_menu'}   
        anchorEl={anchorEl}
        open={ctxMenuOpen}
        X={coordinatesOfMenu.X}
        Y={coordinatesOfMenu.Y}
        onClose={() => setCtxMenuOpen(false)}
      >
        {props.children}
      </EditPanelContextMenu>
    );
  });

  return {
    DataGridContextMenu,
    handleCloseCtxMenu,
    handleContextMenu,
    currentCtxItem,
    actionColumn,
  };
}

export function DataGridCtxMenuAction<T>(
  props: CtxMenuItem<T> & { currentCtxItem: T | undefined, testId?: string } 
) {
  const testId = props.testId ?? 'dataGrid-action';
  console.log('DataGridCtxMenuAction testId:', testId); // Log testId to check its value
  return (
    <MenuItem
      test-id={testId as string  + '_context_menu_item' }  
      onClick={() =>
        props.currentCtxItem && props.onClick
          ? props.onClick(props.currentCtxItem)
          : {}
      }
    >
      <ListItemIcon>
        <IconButton color="primary">{props.icon}</IconButton>
      </ListItemIcon>
      <ListItemText primary={props.label} />
    </MenuItem>
  );
}